import { API_CONFIG } from "@/infrastructure/config/api";
import {
  addAuthTokenToUrl,
  redactTokenInUrl,
} from "@/shared/utils/sseConnection";

export interface LogLine {
  line: string;
  timestamp?: string;
}

export class LogService {
  /**
   * Cria uma conexão SSE para receber logs em tempo real do arquivo de log do servidor
   * @param onMessage Callback para cada nova linha de log
   * @param onConnect Callback executado quando a conexão é estabelecida
   * @param onError Callback para erros na conexão
   */
  static createServerLogStream(
    onMessage: (data: LogLine) => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onConnect?: (connectionData: any) => void,
    onError?: (event: Event) => void,
  ): EventSource {

    // v2 serves system logs at :3333/logs (no /api prefix); legacy at :4000/api/logs.
    const baseUrl = `${API_CONFIG.API_ROOT_URL}/logs/server.log/stream`;

    // Add timestamp to prevent caching
    const urlWithTimestamp = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}_t=${Date.now()}`;

    const sseUrl = addAuthTokenToUrl(urlWithTimestamp);

    // Criar a conexão SSE (token já incluído na URL)
    const eventSource = new EventSource(sseUrl);

    // Evento de conexão estabelecida
    eventSource.addEventListener("connected", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        if (onConnect) {
          onConnect(data);
        }
      } catch (error) {
        console.error(
          "[LogService] Erro ao processar evento connected:",
          error,
        );
      }
    });

    // Evento de recebimento de log
    eventSource.addEventListener("log", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        onMessage(data);
      } catch (error) {
        console.error(
          "[LogService] Erro ao processar log:",
          error,
          (event as MessageEvent).data,
        );
      }
    });

    // Adicionar listener para todos os eventos não capturados especificamente
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        // Se chegou aqui, é possível que o servidor esteja enviando eventos sem o tipo 'log'
        onMessage({ line: JSON.stringify(data) });
      } catch (error) {
        console.error("[LogService] Erro ao processar evento genérico:", error);
      }
    };

    // Evento de erro na conexão
    eventSource.addEventListener("error", (event) => {
      // O evento cru não entra no log: ele expõe `target.url`, que carrega o
      // access token. Ficam só os dados que descrevem a falha.
      console.error("[LogService] Erro na conexão SSE", {
        readyState: eventSource.readyState,
        url: redactTokenInUrl(eventSource.url),
      });

      // Tentar identificar o tipo de erro
      if (eventSource.readyState === EventSource.CLOSED) {
        console.error("[LogService] Conexão SSE foi fechada");
      } else if (eventSource.readyState === EventSource.CONNECTING) {
        console.error("[LogService] Conexão SSE está tentando reconectar");
      }

      if (onError) {
        onError(event);
      }
      // O navegador tentará reconectar automaticamente
    });

    return eventSource;
  }
}
